import React from 'react';

import { Helpers } from '../../core/src/helpers';
import { StoreAppAPI } from '../../core/src/storeapp-api-client';
import SeoMeta from '../../page/components/seo-meta';
import SearchCustomContext from '../../search-filter/components/search-custom-context';
import { findCat } from '../../filters/components/select-categories';
import { withError } from '../../shared/components/error-boundary';

const { memoize, properCase } = Helpers;
const storeAppAPI = new StoreAppAPI();

const NULL_OBJ = {};
const NULL_ARRAY = [];

class VWAMetaData extends React.PureComponent {
    static defaultProps = {
        category: NULL_OBJ,

    }
    getSelectedFacets = memoize((facets, query,limit=3, delimeter=' ') => {
        const { useAlias } = this.props;
        //bucket selection from each facetKey
        let totalSelected = 0;
        let brandIndex = -1;
        let facetBuckets = facets.reduce((result, agg = {}, i) => {
            let facetKey = useAlias ? agg.alias : agg.key;
            let selected = agg.facetValues
                .reduce((f, filter) => {
                    if (filter && filter.selected && filter.displayName) {
                        f.push(
                            properCase({
                                str: filter.title || filter.displayName,
                                capitalize: 'all',
                            })
                        );
                    }
                    return f;
                }, [])
                .sort();

            if (
                selected.length > 0 &&
                (!query || `facets.${facetKey}` in query)
            ) {
                brandIndex = agg.key === 'brand' ? i : brandIndex;
                result.push(selected);
                totalSelected += selected.length;
            }
            return result;
        }, []);

        //loop through buckets, add first selected facets from each bucket
        //continue till limit is reached or none are left
        let selectedFacets = [];
        let loopLimit = facetBuckets.length * totalSelected;
        for (var i = 0; i < loopLimit; i++) {
            if (totalSelected == 0 || selectedFacets.length === limit) {
                break;
            }
            let bucket = facetBuckets[i % facetBuckets.length];
            if (bucket.length > 0) {
                selectedFacets.push(bucket.shift());
                totalSelected--;
            }
        }

        return { selectedFacets, brandIndex };
    });

    logCanonical = memoize((canonicalQuery) => {
        const {
            urlFormatter,
        } = this.props;
        storeAppAPI.logger.log({
            message:  `https://store.hp.com${urlFormatter(canonicalQuery)}`,
            info: `plp-canonical`,
            level: 'info',
        });
        return canonicalQuery;
    },(canonicalQuery) =>  {
        const {
            urlFormatter,
        } = this.props;
        return `https://store.hp.com${urlFormatter(canonicalQuery)}`
    })

    /**
     * Returns facets to use for generating dynamic seo values
     */
    getSEOData = (facets = [], seoFacets = {}) => {
        let { useAlias } = this.props;
        let {
            overheadFacetKeys = [],
            facetPriority = [],
            canonicalFacet
        } = seoFacets;
        let facetKeyMap = {};
        let overheadFacetMap = {};
        let relevantFacets = facets
            .reduce((r, f, i) => {
                let mapKey = useAlias ? f.alias : f.key;
                //map vanity value to id to help with create canonical
                facetKeyMap[mapKey] = f._id;
                if (!overheadFacetKeys.includes(f._id)) {
                    //add a priority to facet to sort it
                    let priorityIndex = facetPriority.indexOf(f._id);
                    //if facet exists in priority list then use it's index for sort
                    //otherwise offset priorty  using current index
                    f.seoPriority =
                        priorityIndex > -1
                            ? priorityIndex
                            : facetPriority.length + i;
                    r.push(f);
                } else {
                    overheadFacetMap[mapKey] = f._id;
                }
                if (canonicalFacet === f._id) {
                    canonicalFacet = useAlias ? f.alias : f.key;
                }
                return r;
            }, [])
            .sort((a, b) => a.seoPriority - b.seoPriority);
            
        return {
            relevantFacets,
            facetKeyMap,
            canonicalFacet,
            overheadFacetMap,
            facetPriority,
        };
    };

    getTitle = (
        title,
        selectedFacets,
        brandIndex,
        canonicalFacetSelect,
        categoryTitle,
        categoryTitlePosition = 'suffix',
        delimeter = ' '
    ) => {
        if (title && title !== 'HP® Official Store') {
            return properCase({
                str: title,
                capitalize: 'all',
            });
        }
        if (categoryTitlePosition === 'omit' && canonicalFacetSelect) {
            //do nothing
        } else if(categoryTitlePosition === 'prefix') {
            //insert category name after brand if it exists, otherwise it will be first
            selectedFacets.splice(brandIndex + 1,0, categoryTitle)
        }else{
            selectedFacets.push(categoryTitle)
        }

        return selectedFacets.join(delimeter).replace(/\s\s+/gi, ' ').trim();
    };

    getDescription = (description, title, category = {}) => {
        if (description) {
            return description;
        }
        return `Save on ${title} with Free Shipping when you buy now online. Get our best deals on ${category.displayName} when you shop with HP.`.replace(
            /\s\s+/gi,
            ' '
        );
    };

    /**
     * Reduce down query to canonical query based specification from the SEO team
     */
    getCanonicalQuery = (query, canonicalFacet, overheadFacetMap) => {
        //if overhead reached, canonicalize to either root or "tier3" facet
        let atOverhead = false;
        let rootCanonical;
        let facetCount = 0;
        
        let canonicalQuery = Object.keys(query).reduce((canQuery, key) => {
            let value = query[key];
            let [, facetKey] = key.split('.');
            if (key === 'cat') {
                canQuery[key] = value;
            } else {
                if (value.length > 1) {
                    atOverhead = true;
                } else if(!(facetKey in overheadFacetMap)){
                    facetCount += 1;
                    //else set rootCanincal if it's is selected in the query
                    facetKey === canonicalFacet && (rootCanonical = key);
                    canQuery[key] = value;
                }
            }

            return canQuery;
        }, {});

        if (atOverhead || facetCount > 3) {
            let rootQuery = { cat: query.cat };
            //if "tier 3" canonical selected include that in root
            rootCanonical && (rootQuery[rootCanonical] = query[rootCanonical]);
            return { query: rootQuery };
        } else {
            return { query: canonicalQuery };
        }
    };

    render() {
        const {
            title,
            canonical,
            keywords,
            robots,
            description,
            urlFormatter,
            category,
            facets,
            query,
            etrCanonical
        } = this.props;
        let { seo = NULL_OBJ } = category;
        let { facets: seoFacets, titlePosition: categoryTitlePosition, title: categoryTitle, titleVariant } = seo;
        let { relevantFacets, canonicalFacet, overheadFacetMap } = this.getSEOData(
            facets,
            seoFacets
        );
        
        let canonicalFacetSelect = `facets.${canonicalFacet}` in query;
        let canonicalQuery = this.getCanonicalQuery(query, canonicalFacet, overheadFacetMap);
        //TEMP REMOVE THIS, LOGGING FOR SITEMAP
        const tempLog = this.logCanonical(canonicalQuery);
        let { selectedFacets, brandIndex } = this.getSelectedFacets(relevantFacets, query, 3);
        //if tier 3 facet selected and titleVariant provided use that otherwise use default
        let catTitle = (canonicalFacetSelect && titleVariant) || categoryTitle || category.displayName;
        let seoTitle = this.getTitle(
            title,
            selectedFacets,
            brandIndex,
            canonicalFacetSelect,
            catTitle,
            categoryTitlePosition
        );

        let dynamicSeo = {
            title: seoTitle,
            canonical:
                etrCanonical || canonical ||
                `https://store.hp.com${urlFormatter(canonicalQuery)}`,
            description: this.getDescription(description, seoTitle, category),
            keywords: keywords,
            robots: robots,
        };

        return <SeoMeta seo={dynamicSeo} />;
    }
}

class VWAMetaDataContainer extends React.PureComponent {
    urlFormatter = memoize((customAggregations) => {
        const { match, urlFormatter } = this.props;
        return (query) => {
            return urlFormatter(match, query, customAggregations || {});
        };
    });


    render(){
        const { seo, useAlias, pathname } = this.props;
        //if the seo path not match the current path, do not use the SEO values for title, canonical, or description
        if (seo.path !== pathname) {
            seo.title = undefined;
            seo.canonical = undefined;
            seo.description = undefined;
        }

        return (
            <SearchCustomContext>
                {({ customAggregations, customContext={}, query }) => {
                    {
                        let category =
                            useAlias && customAggregations
                                ? customAggregations.category
                                : findCat(query.cat, customContext.categories);
                        let { facets = NULL_ARRAY } =
                            customAggregations || NULL_OBJ;
                        return (
                            <VWAMetaData
                                useAlias={useAlias}
                                category={category}
                                facets={facets}
                                urlFormatter={this.urlFormatter(
                                    customAggregations
                                )}
                                query={query}
                                title={seo.title}
                                description={seo.description}
                                canonical={seo.canonical}
                                keywords={seo.keywords}
                                robots={seo.robots}
                                etrCanonical={customAggregations && customAggregations.canonical}
                            />
                        );
                    }
                }}
            </SearchCustomContext>
        );
    }
}

export default withError(VWAMetaDataContainer);
